import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PageData from '@v4/utils/data/PageData'
import useShortcuts from '@v4/hooks/useShortcuts'
import ColorsSelector from '@v4/utils/selectors/ColorsSelector'
import { useSectionActions } from '@v4/components/common/SectionItem'
import { useStoreActions } from 'easy-peasy'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import { getFontSize } from '@v4/utils/theme'
import PreviewSelector from '@v4/utils/selectors/PreviewSelector'
import AIAssistantSelector from '@v4/utils/selectors/AIAssistantSelector'
import { getLinkList, getMergeTagsList, isHtmlEmpty, setContainerPosition } from './useEditor.utils'
import { EDITOR_VARIANT, FONT_SIZES, STYLES_FORMAT, LINE_HEIGHTS_FORMAT, AI_SHORTCUTS } from './useEditor.const'
import useStyles from './useEditor.styled'

const useEditor = ({
	active,
	value,
	parsedValue,
	extraShortcuts,
	onChange,
	onFocus,
	onBlur,
	variant = EDITOR_VARIANT.FULL,
	allowEmpty = false,
	clickOnFocus = true,
	validElements,
	categoryScoreMergeTags,
	leadAnswerMergeTags,
	quizMergeTags,
	autoFocus = false,
}) => {
	const isMobilePreview = PreviewSelector.useIsMobileView()
	const { fetchEditorAiContent } = AIAssistantSelector.useActions()
	const [editorActive, setEditorActive] = useState(false)
	const elementRef = useRef(null)
	const editorRef = useRef(null)
	const shortcuts = useShortcuts(extraShortcuts)
	const [focused, setFocused] = useState(false)
	const [dragging, setDragging] = useState(false)
	const settings = ScorecardSelector.useSettings()
	const directionality = settings?.locale_text_direction || 'ltr'
	const colors = ColorsSelector.usePreset()
	const { addRecent } = ColorsSelector.useActions()
	const currentValue = focused ? value : parsedValue
	const withValue = !!currentValue || allowEmpty
	const { handleMouseEnter, handleMouseLeave } = useSectionActions()
	const classes = useStyles({ focused, variant })
	const classesRef = useRef(classes)
	classesRef.current = classes
	const onChangeRef = useRef(onChange)
	onChangeRef.current = onChange
	const onFocusRef = useRef(onFocus)
	onFocusRef.current = onFocus
	const onBlurRef = useRef(onBlur)
	onBlurRef.current = onBlur

	const upload = useStoreActions(actions => actions.gallery.upload.request)
	const uploadRef = useRef(upload)

	uploadRef.current = upload

	const isPageGalleryActive = PageBuilderSelector.useIsGalleryActive()
	const isQuestionBuilderMode = PageBuilderSelector.useIsQuestionsMode()
	const isGalleryActive = isPageGalleryActive || isQuestionBuilderMode

	const pageType = PageBuilderSelector.useType()
	const isResultPdf = PageData.isTypeResultPdf(pageType)
	const isResult = PageData.isTypeResult(pageType)
	const isLanding = PageData.isTypeLanding(pageType)

	const withAiAssistant = isResultPdf || isResult || isLanding

	const isMobileView = !isResultPdf && !isQuestionBuilderMode && isMobilePreview
	const isMobileViewRef = useRef(isMobileView)
	isMobileViewRef.current = isMobileView

	const isGalleryActiveRef = useRef(isGalleryActive)
	isGalleryActiveRef.current = isGalleryActive

	const $container = useMemo(() => {
		if (variant === EDITOR_VARIANT.SIMPLE || !active) {
			return null
		}
		const $element = document.createElement('div')
		document.body.appendChild($element)

		return $element
	}, [variant, active])

	const valueRef = useRef(currentValue)

	valueRef.current = currentValue ?? ''

	const handleSetContainerPosition = useCallback(() => {
		if (!$container) {
			return
		}

		const mobile = isMobileViewRef.current

		if (mobile) {
			$container.dataset.view = 'mobile'
		} else {
			$container.removeAttribute('data-view')
		}

		const selectedNode = editorRef.current?.selection?.getNode()

		setContainerPosition(
			$container,
			elementRef.current,
			classesRef.current,
			mobile ? EDITOR_VARIANT.MOBILE : variant,
			mobile ? selectedNode : null,
		)
	}, [$container, variant])

	const handleChange = useCallback(
		event => {
			const editor = editorRef.current

			if (event?.type === 'keydown' && isMobileViewRef.current) {
				if (![37, 38, 39, 40].includes(event.keyCode)) {
					event.preventDefault()
					event.stopPropagation()
					return false
				}
			}

			if (!editor || !onChangeRef.current || !editor.hasFocus()) {
				return undefined
			}

			handleSetContainerPosition()

			let newValue = editor.getContent()
			if (isHtmlEmpty(newValue)) {
				newValue = '​'
			}
			if (newValue !== valueRef.current) {
				onChangeRef.current(newValue)
			}
			return undefined
		},
		[handleSetContainerPosition],
	)

	const handleClick = useCallback(event => {
		event.target.focus()
	}, [])

	const handleFocus = useCallback(
		event => {
			setFocused(true)
			onFocusRef.current?.(event)
			handleSetContainerPosition()
		},
		[handleSetContainerPosition],
	)
	const handleBlur = useCallback(
		event => {
			setFocused(false)
			const content = editorRef.current?.getContent()
			if (content === '<p>​</p>' || content === '<div>​</div>' || content === '​') {
				onChangeRef.current('')
				editorRef.current?.resetContent('')
			} else {
				editorRef.current?.resetContent(valueRef.current)
			}
			onBlurRef.current?.(event)
		},
		[onBlurRef, onChangeRef],
	)

	const handleDragOver = useCallback(
		event => {
			if (!dragging) {
				onFocusRef.current?.(event)
				setDragging(true)
			}
		},
		[dragging],
	)

	const handleDragleave = useCallback(event => {
		const editor = editorRef.current

		if (editor) {
			setDragging(false)
			editor.getBody().blur()
			onBlurRef.current?.(event)
		}
	}, [])

	const handlePreventEventOnMobile = useCallback(event => {
		if (isMobileViewRef.current) {
			event.preventDefault()
			event.stopPropagation()
		}
	}, [])

	const handleBeforeSetContent = useCallback(event => {
		if (!isMobileViewRef.current) {
			return undefined
		}

		if (event.initial) return true
		if (!event.source || event.source !== 'mobileFontSize') {
			event.preventDefault()
			return false
		}
		return undefined
	}, [])

	const handleBeforeExecCommand = useCallback(event => {
		if (isMobileViewRef.current && !['mceRepaint'].includes(event.command)) {
			event.preventDefault()
			return false
		}
		return undefined
	}, [])

	const handleSelect = useCallback(() => {
		const selection = editorRef.current?.selection
		if (!$container || !selection) {
			return
		}

		const mobile = isMobileViewRef.current

		$container.style.display = !mobile || !selection.isCollapsed() ? 'block' : 'none'

		handleSetContainerPosition()
	}, [$container, handleSetContainerPosition])

	useEffect(() => {
		const element = elementRef.current
		if (!withValue || !active || !element || !window.tinymce) {
			return undefined
		}

		const changeEvents = () => {
			const isIE = window.tinymce.Env?.browser?.isIE()
			return isIE
				? 'change keyup compositionend setcontent CommentChange Change'
				: 'change input compositionend setcontent CommentChange Change'
		}

		element.addEventListener('click', handleClick)

		const baseConfig = {
			target: element,
			inline: true,
			menubar: false,
			directionality,
			statusbar: false,
			toolbar_mode: 'wrap',
			browser_spellcheck: true,
			contextmenu: false,
			fix_list_elements: true,
			entities: '38,amp,60,lt,62,gt',
			indentation: '2rem',
			entity_encoding: 'raw',
			remove_script_host: false,
			convert_urls: false,
			mobile: {
				toolbar_mode: 'wrap',
			},
			image_uploadtab: false,
			visualblocks_default_state: false,
			branding: false,
			skin_url: `${import.meta.env.VITE_ASSET_URL}/css/v4/editor`,
			paste_preprocess: (editor, args) => {
				try {
					const div = document.createElement('div')

					div.innerHTML = args.content

					if ([EDITOR_VARIANT.SIMPLE, EDITOR_VARIANT.SHORT, EDITOR_VARIANT.EMPHASIS].includes(variant)) {
						// eslint-disable-next-line no-param-reassign
						args.content = div.innerText
					} else {
						const images = div.querySelectorAll('img')
						_.forEach(images, image => {
							const img = image
							img.style.width = '100%'
							img.style.height = 'auto'
							img.style.maxWidth = '100%'
							img.style.objectFit = 'contain'
							img.setAttribute('data-mce-style', `${img.getAttribute('style')}`)
						})

						const elements = div.querySelectorAll('[class], [style]')

						_.forEach(elements, el => {
							el.removeAttribute('class')
							el.removeAttribute('style')
						})

						// eslint-disable-next-line no-param-reassign
						args.content = div.innerHTML
					}
					// eslint-disable-next-line no-empty
				} catch (error) {}
			},
			images_upload_handler: blobInfo =>
				new Promise((resolve, reject) => {
					const blob = blobInfo.blob()
					const file = new File([blob], blobInfo.filename(), {
						type: blob.type,
					})
					if (!isGalleryActiveRef.current) {
						resolve('')
						return undefined
					}
					if (!uploadRef.current) {
						// eslint-disable-next-line prefer-promise-reject-errors
						reject({
							message: 'Upload handler is not defined',
							remove: true,
						})
						return undefined
					}
					return uploadRef
						.current([file])
						.then(assets => {
							resolve(
								// eslint-disable-next-line max-len
								`https://cdn.scoreapp.com/cdn-cgi/image/onerror=redirect,format=auto,width=1280,quality=75,fit=scale-down/${assets[0].path}`,
							)
						})
						.catch(error => {
							// eslint-disable-next-line prefer-promise-reject-errors
							reject({
								message: error.message || 'Something went wrong',
								remove: true,
							})
						})
				}),
			setup: editor => {
				setEditorActive(true)
				editorRef.current = editor
				window.editor = editor

				editor.on('init', () => {
					const body = editor.getBody()
					body.addEventListener('dragleave', handleDragleave)
					if (autoFocus) {
						editor.focus()
					}
				})
				editor.on(changeEvents(), handleChange)
				editor.on('keypress', handleChange)
				editor.on('keydown', handleChange)
				editor.on('NewBlock', handleChange)
				editor.on('FormatApply', handleChange)
				editor.on('focus', handleFocus)
				editor.on('blur', handleBlur)
				editor.on('dragover', handleDragOver)
				editor.on('SelectionChange', handleSelect)

				editor.on('contextmenu', handlePreventEventOnMobile)
				editor.on('paste', handlePreventEventOnMobile)
				editor.on('drop', handlePreventEventOnMobile)

				editor.on('BeforeSetContent', handleBeforeSetContent)
				editor.on('BeforeExecCommand', handleBeforeExecCommand)
				editor.on('AIDialogOpen', () => {
					setTimeout(() => {
						const dialogEl = document.querySelector('.tox-dialog')

						if (!dialogEl) return

						dialogEl.style.left = ''
						dialogEl.style.top = ''
						dialogEl.style.right = ''
						dialogEl.style.bottom = ''
						dialogEl.style.position = 'fixed'
						dialogEl.classList.add('ai-assistant')

						const { innerWidth, innerHeight } = window
						const { offsetWidth, offsetHeight } = dialogEl

						const left = (innerWidth - offsetWidth) / 2
						const top = (innerHeight - offsetHeight) / 2

						dialogEl.style.left = `${left}px`
						dialogEl.style.top = `${top}px`
					}, 0)
				})

				editor.once('PostRender', () => {
					editor.setContent(currentValue, { no_events: true, initial: true })
					editor.undoManager?.reset()
					setTimeout(() => {
						if (editorRef.current?.targetElm) {
							editorRef.current.targetElm.innerHTML = parsedValue
						}
					}, 100)
				})

				const createOnSetup = fontSize =>
					function test(api) {
						const range = editor.selection?.getRng()
						const startContainer = range?.startContainer

						if (!startContainer) {
							return () => {}
						}

						const firstElement =
							startContainer.nodeType === Node.ELEMENT_NODE
								? startContainer
								: startContainer.parentElement

						if (!firstElement) {
							return () => {}
						}

						const { fontSize: fontSizeCss } = window.getComputedStyle(firstElement)
						const fontSizeValue = Math.round(+fontSizeCss.replace('px', ''))

						if (fontSize === fontSizeValue) {
							api.setActive(true)
						}

						return () => {}
					}

				editor.ui.registry.addMenuButton('fonts', {
					icon: 'change-case',
					fetch: callback => {
						const items = _.map(FONT_SIZES, fontSize => ({
							type: 'togglemenuitem',
							text: getFontSize(fontSize),
							onSetup: createOnSetup(fontSize),
							onAction: () => {
								editor.execCommand('FontSize', false, getFontSize(fontSize, 'rem'))
							},
						}))

						callback(items)
					},
				})

				if (!isResultPdf) {
					editor.ui.registry.addMenuButton('mobile-fonts', {
						icon: 'change-case',
						fetch: callback => {
							const items = _.map(FONT_SIZES, fontSize => ({
								type: 'togglemenuitem',
								text: getFontSize(fontSize),
								onSetup: createOnSetup(fontSize),
								onAction: () => {
									editor.formatter.apply('mobileFontSize', { value: fontSize })
								},
							}))

							callback(items)
						},
					})
				}

				editor.on('ExecCommand', event => {
					if (event.command === 'mceApplyTextcolor') {
						addRecent(event.value)
					}
					if (!isResultPdf && event.command === 'RemoveFormat') {
						editor.formatter.remove('removeMobileFontSize')
					}
				})

				editor.on('ObjectResized', ({ width, height, target }) => {
					const image = target
					image.style.width = `${width / 16}rem`
					image.style.height = `${height / 16}rem`
					image.style.maxWidth = '100%'
					image.style.objectFit = 'contain'

					image.setAttribute('data-mce-style', `${image.getAttribute('style')}`)
				})
			},
		}

		if (validElements) {
			baseConfig.valid_elements = validElements
		}

		if (isResultPdf) {
			baseConfig.link_target_list = false
		}

		const getConfig = () => {
			switch (variant) {
				case EDITOR_VARIANT.SIMPLE:
					return {
						...baseConfig,
						toolbar: false,
						valid_elements: 'br',
						forced_root_block: 'div',
					}
				case EDITOR_VARIANT.EMPHASIS:
				case EDITOR_VARIANT.SHORT: {
					const isEmphasis = variant === EDITOR_VARIANT.EMPHASIS

					return {
						...baseConfig,
						forced_root_block: 'div',
						link_list: getLinkList(shortcuts),
						color_cols: 8,
						fixed_toolbar_container_target: $container,
						color_map: _.reduce(
							colors,
							(acc, color) => {
								acc.push(color, '')
								return acc
							},
							[],
						),
						font_size_formats: _.map(FONT_SIZES, fontSize => getFontSize(fontSize)).join(', '),
						plugins: isEmphasis ? ['autoresize'] : ['autoresize', 'autolink', 'link'],
						toolbar: isEmphasis ? 'bold italic underline' : 'bold italic underline | link',
					}
				}
				default:
					return {
						...baseConfig,
						external_plugins: {
							// eslint-disable-next-line max-len
							mergetags: `${import.meta.env.VITE_ASSET_URL}/js/v4/editor-plugins/mergetags.js?v=2`,
							...(withAiAssistant && {
								ai: `https://cdn.tiny.cloud/1/${
									import.meta.env.VITE_EDITOR_KEY
								}/tinymce/6.8.6-46/plugins/ai/plugin.min.js`,
							}),
						},
						ai_request: (request, respondWith) => {
							respondWith.stream((signal, streamMessage) => {
								return fetchEditorAiContent({
									request,
									streamMessage,
									signal,
								})
							})
						},
						ai_shortcuts: AI_SHORTCUTS,
						mergetags_list: getMergeTagsList(shortcuts, {
							categoryScoreMergeTags,
							leadAnswerMergeTags,
							quizMergeTags,
						}),
						link_list: getLinkList(shortcuts),
						end_container_on_empty_block: true,
						color_cols: 8,
						fixed_toolbar_container_target: $container,
						color_map: _.reduce(
							colors,
							(acc, color) => {
								acc.push(color, '')
								return acc
							},
							[],
						),
						plugins: ['autoresize', 'autolink', 'lists', 'link', 'image', 'visualblocks', 'code'],
						mergetags_prefix: '{',
						mergetags_suffix: '}',
						formats: {
							primary: { block: 'span', classes: 'p-primary' },
							mobileFontSize: {
								inline: 'span',
								attributes: { 'data-mfs': '%value' },
							},
							removeMobileFontSize: {
								inline: 'span',
								attributes: ['data-mfs'],
								remove: 'all',
							},
						},
						style_formats: STYLES_FORMAT,
						font_size_formats: _.map(FONT_SIZES, fontSize => getFontSize(fontSize)).join(', '),
						line_height_formats: LINE_HEIGHTS_FORMAT.join(' '),
						toolbar1:
							'styles | bold italic underline | forecolor fonts lineheight' +
							' | alignleft aligncenter alignright | numlist bullist outdent indent ' +
							' | link removeformat mergetags | aidialog aishortcuts',
						toolbar2: isResultPdf ? undefined : 'mobile-fonts | | test-fonts',
					}
			}
		}

		const config = getConfig()

		window.tinymce.init(config)

		return () => {
			const editor = editorRef.current
			if (!editor) {
				return
			}
			editor.off(changeEvents(), handleChange)
			editor.off('keypress', handleChange)
			editor.off('keydown', handleChange)
			editor.off('NewBlock', handleChange)
			editor.off('FormatApply', handleChange)
			editor.off('focus', handleFocus)
			editor.off('blur', handleBlur)
			editor.off('dragover', handleDragOver)

			editor.off('contextmenu', handlePreventEventOnMobile)
			editor.off('paste', handlePreventEventOnMobile)
			editor.off('drop', handlePreventEventOnMobile)
			editor.off('BeforeSetContent', handleBeforeSetContent)
			editor.off('BeforeExecCommand', handleBeforeExecCommand)
			editor.off('SelectionChange', handleSelect)

			const body = editor.getBody()
			body?.removeEventListener('dragleave', handleDragleave)

			// eslint-disable-next-line react-hooks/exhaustive-deps
			elementRef.current?.removeEventListener('click', handleClick)

			window.tinymce.remove(editor)
			editorRef.current = null
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		withValue,
		active,
		variant,
		handleChange,
		handleFocus,
		handleBlur,
		addRecent,
		handleMouseEnter,
		handleMouseLeave,
		handleSelect,
	])

	useEffect(() => {
		const editor = editorRef.current

		if (!editor || editor.getContent() === currentValue) {
			return
		}

		const setValue = newValue => editor.setContent(newValue, { no_events: true })

		if (editor.hasFocus()) {
			let cursor
			try {
				cursor = editor.selection.getBookmark(3)
				// eslint-disable-next-line no-empty
			} catch (e) {}

			setValue(currentValue)
			try {
				if (cursor) {
					editor.selection.moveToBookmark(cursor)
				}
				// eslint-disable-next-line no-empty
			} catch (e) {}
		} else if (!focused && editorRef.current?.targetElm) {
			editorRef.current.targetElm.innerHTML = currentValue
		} else {
			setValue(currentValue)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentValue])

	useEffect(() => {
		if (focused && !dragging) {
			editorRef.current?.setContent(currentValue, { no_events: true })
			editorRef.current?.undoManager?.reset()
			if (clickOnFocus) {
				elementRef.current?.click()
			}

			if (!$container) {
				return undefined
			}

			window.addEventListener('resize', handleSetContainerPosition)
			document.addEventListener('scroll', handleSetContainerPosition)

			return () => {
				window.removeEventListener('resize', handleSetContainerPosition)
				document.removeEventListener('scroll', handleSetContainerPosition)
			}
		}
		const timeout = setTimeout(() => {
			if (editorRef.current?.targetElm) {
				editorRef.current.targetElm.innerHTML = parsedValue
			}
		}, 0)

		return () => {
			clearTimeout(timeout)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focused])

	useEffect(() => {
		if (!$container) {
			return undefined
		}

		$container.addEventListener('mouseenter', handleMouseEnter)
		$container.addEventListener('mouseleave', handleMouseLeave)
		return () => {
			$container.removeEventListener('mouseenter', handleMouseEnter)
			$container.removeEventListener('mouseleave', handleMouseLeave)
			$container.remove()
		}
	}, [$container, handleMouseEnter, handleMouseLeave])

	return {
		active: editorActive,
		elementRef,
		editorRef,
	}
}

export default useEditor
